/**
 * Which uptime metric an org displays as "the" uptime across the platform.
 * The org field itself (fleet-management `primaryUptimeDisplay`) may also be 'INHERIT'
 * (its column default), meaning the org defers to its parent organization's setting;
 * the backend resolves that to `effectivePrimaryUptimeDisplay`, which is never INHERIT.
 */
export type PrimaryUptimeDisplay = 'UPTIME_INCLUDING_OFFLINE' | 'UPTIME_WHILE_ONLINE'

export const DEFAULT_PRIMARY_UPTIME_DISPLAY: PrimaryUptimeDisplay = 'UPTIME_INCLUDING_OFFLINE'

/**
 * Narrow the org API's (generated as plain `string`) effective uptime display to the
 * display union. Anything other than while-online — including INHERIT slipping through or an
 * unknown value — resolves to the historical metric.
 */
export function toPrimaryUptimeDisplay(value: string | undefined): PrimaryUptimeDisplay {
  return value === 'UPTIME_WHILE_ONLINE' ? 'UPTIME_WHILE_ONLINE' : DEFAULT_PRIMARY_UPTIME_DISPLAY
}

type UptimeMetricPair = {
  uptimePercentage?: number
  uptimeWhileOnlinePercentage?: number
}

/**
 * Picks the uptime percentage to display based on the org's primary uptime display setting.
 *
 * When the while-online metric is selected but absent (the chargers had no online time in the
 * window), this returns undefined rather than 0 — "no data while online" must not read as
 * "0% uptime".
 */
export function pickUptimePercentage(
  metrics: UptimeMetricPair,
  display: PrimaryUptimeDisplay
): number | undefined {
  if (display === 'UPTIME_WHILE_ONLINE') return metrics.uptimeWhileOnlinePercentage ?? undefined
  return metrics.uptimePercentage
}
